import {
  useGetAuthorConfiguratorBundleQuery,
  useGetVariantMatrixQuery,
  useListColourQuery,
  useListPackageQuery,
  useListUpholsteryQuery,
} from '@dms/api/new-vehicle';

import {variantChangeName} from '../utils/variantChangeName';

const ADJUSTABLE_SCOPES = [
  'trim',
  'variant',
  'colour',
  'upholstery',
  'package',
  'feature',
] as const;
export type AdjustableScope = (typeof ADJUSTABLE_SCOPES)[number];
export type AdjustableItems = Record<AdjustableScope, {value: string; label: string}[]>;

/** Every item of a model year a price adjustment can reprice, labelled as the catalogue shows it. */
export function useAdjustableItems(modelYearId: string, options: {skip: boolean}) {
  const matrix = useGetVariantMatrixQuery({modelYearId}, options);
  const colours = useListColourQuery({}, options);
  const upholsteries = useListUpholsteryQuery({}, options);
  const packages = useListPackageQuery({}, options);
  const bundle = useGetAuthorConfiguratorBundleQuery({modelYearId}, options);
  const ofYear = <T extends {modelYearId: string; archived: boolean; id: string; name: string}>(
    rows: T[] | undefined
  ) =>
    (rows ?? [])
      .filter((row) => row.modelYearId === modelYearId && !row.archived)
      .map(({id, name}) => ({value: id, label: name}));
  const trims = matrix.data?.trims ?? [];
  const engines = matrix.data?.engines ?? [];
  const nameOf = (rows: {id: string; name: string}[], id: string) =>
    rows.find((row) => row.id === id)?.name ?? '—';
  const items: AdjustableItems = {
    trim: trims.map(({id, name}) => ({value: id, label: name})),
    variant: (matrix.data?.cells ?? [])
      .filter((variant) => !variant.archived)
      .map((variant) => ({
        value: variant.id,
        label: variantChangeName(
          nameOf(trims, variant.trimId),
          nameOf(engines, variant.engineId),
          variant.orderCode
        ),
      })),
    colour: ofYear(colours.data?.items),
    upholstery: ofYear(upholsteries.data?.items),
    package: ofYear(packages.data?.items),
    feature: (bundle.data?.features ?? [])
      .filter(({trimInclusions}) => trimInclusions.some(({inclusion}) => inclusion === 'OPTIONAL'))
      .map(({id, name}) => ({value: id, label: name})),
  };
  return {
    items,
    isLoading:
      matrix.isLoading ||
      colours.isLoading ||
      upholsteries.isLoading ||
      packages.isLoading ||
      bundle.isLoading,
  };
}
